import { useCallback, useMemo, useRef, useState } from 'react'
import { QueryClient, useQuery } from 'react-query'
import type { DropdownOption, SortOption, SpaceType } from '@studio/atomic-ui'
import { mapSortByType, priceSanityField } from './showcase-utils'
import { SanityBusinessHours, SanitySmProduct } from '../../services/sanity/types'
import { PlpSortStrategy } from '../../services/api/types'
import { getBuildingHolidays } from '../../services/api/holidays'
import { getAllSanityProducts, getCountSanityProducts, getSanityProducts } from '../../services/api/products'
import { getBusinessHours } from '../../utils/business-hours'

type UseProductShowcaseFetchType = {
  capacity: DropdownOption | undefined
  location: string | undefined
  building: string | undefined
  type: SpaceType | undefined // Deprecated: use types instead
  types: SpaceType[] | undefined // Array of space types
  propsBuilding: string | undefined
  propsRegion: string | undefined
  instantBook: boolean
  sortBy: SortOption
  buildingListCall: () => string[] | null
  onProductsUpdate: (products: SanitySmProduct[]) => Promise<void>
  /** Server-prefetched products — when provided, skip the first client-side fetch */
  initialProducts?: SanitySmProduct[]
  /** PLP sort strategy from A/B test */
  sortStrategy?: PlpSortStrategy
}

export enum LoadingState {
  IDLE = 'IDLE',
  FETCHING_MORE_PRODUCTS = 'FETCHING_MORE_PRODUCTS',
  FETCHING_NEW_PRODUCTS = 'FETCHING_NEW_PRODUCTS',
  LOAD_ERROR = 'LOAD_ERROR',
}

export const useProductShowcaseFetch = ({
  capacity,
  location,
  building,
  type,
  types,
  sortBy,
  buildingListCall,
  onProductsUpdate,
  instantBook,
  initialProducts,
  sortStrategy = PlpSortStrategy.Standard,
}: UseProductShowcaseFetchType) => {
  // Memoize currentTypes array, handling backward compatibility
  const currentTypes = useMemo((): SpaceType[] => {
    if (types && types.length > 0) {
      return types
    }
    if (type) {
      return [type]
    }
    return ['meeting_room']
  }, [types, type])

  const typesKey = useMemo(() => currentTypes.join(','), [currentTypes])

  const hasMeetingRoom = useMemo(() => currentTypes.includes('meeting_room'), [currentTypes])

  const queryClient = useMemo(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            staleTime: Infinity,
          },
        },
      }),
    []
  )
  // When initial products are provided by the server, skip the first fetch and start in IDLE.
  const hasInitialProducts = initialProducts && initialProducts.length > 0
  const [loadingState, setLoadingState] = useState<LoadingState>(
    hasInitialProducts ? LoadingState.IDLE : LoadingState.FETCHING_NEW_PRODUCTS
  )
  const [productsSize, setProductsSize] = useState(hasInitialProducts ? initialProducts.length : 0)
  const [businessHours, setBusinessHours] = useState<SanityBusinessHours>({
    startHour: '09:00',
    endHour: '17:00',
  })
  const productsData = useRef<SanitySmProduct[]>(hasInitialProducts ? initialProducts : [])
  const lastProductCopy = useRef<SanitySmProduct | null>(
    hasInitialProducts ? initialProducts[initialProducts.length - 1] : null
  )
  // Track whether the first fetch should be skipped (when SSR-prefetched data exists)
  const skipFirstFetch = useRef(!!hasInitialProducts)
  // Concurrency guard — prevents overlapping fetchProductsPaginated calls
  const isFetching = useRef(false)
  const lastQueryKey = useRef<{
    location: string | undefined
    building: string | undefined
    typesKey: string | undefined
    sortBy: SortOption | undefined
    capacity: DropdownOption | undefined
    instantBook: boolean | undefined
    sortStrategy: PlpSortStrategy
  }>({
    location: undefined,
    building: undefined,
    typesKey: undefined,
    sortBy: undefined,
    capacity: undefined,
    instantBook: undefined,
    sortStrategy: PlpSortStrategy.Standard,
  })

  const fetchProductsCount = useCallback(async () => {
    try {
      const data = await queryClient.fetchQuery(
        ['productSize', location, building, typesKey, capacity, instantBook],
        async () => {
          return await getCountSanityProducts({
            capacity: capacity?.value,
            type: currentTypes,
            buildings: buildingListCall(),
            instantBook: instantBook,
          })
        },
        {
          staleTime: 5 * 60 * 1000,
        }
      )
      setProductsSize(data)
    } catch (error) {
      setProductsSize(0)
    }
  }, [queryClient, location, building, typesKey, capacity, instantBook, currentTypes, buildingListCall])

  const resetPagination = useCallback(() => {
    lastProductCopy.current = null
  }, [])

  const fetchProductsPaginated = useCallback(async () => {
    if (isFetching.current) return
    isFetching.current = true
    try {
      // Skip the first client-side fetch when server-prefetched products exist.
      // This eliminates the fetch waterfall (HTML → JS → API → render) and lets
      // the first 8 product cards appear in the SSR HTML immediately.
      if (skipFirstFetch.current) {
        skipFirstFetch.current = false
        lastQueryKey.current = { location, building, typesKey, sortBy, capacity, instantBook, sortStrategy }
        // Still fetch the real count in the background so pagination is accurate
        fetchProductsCount()
        return
      }

      // Compare current typesKey with previous one directly
      const isLoadingNewData =
        location !== lastQueryKey.current.location ||
        building !== lastQueryKey.current.building ||
        typesKey !== lastQueryKey.current.typesKey ||
        sortBy !== lastQueryKey.current.sortBy ||
        capacity !== lastQueryKey.current.capacity ||
        instantBook !== lastQueryKey.current.instantBook ||
        sortStrategy !== lastQueryKey.current.sortStrategy

      if (isLoadingNewData) {
        setLoadingState(LoadingState.FETCHING_NEW_PRODUCTS)
        resetPagination()
        await fetchProductsCount()
      } else {
        setLoadingState(LoadingState.FETCHING_MORE_PRODUCTS)
      }

      const data = await queryClient.fetchQuery(
        ['product', location, building, typesKey, sortBy, lastProductCopy.current, capacity, instantBook, sortStrategy],
        async () => {
          const buildings = buildingListCall()
          const { field } = mapSortByType(sortBy.value)

          let lastFieldValue: string | number | null = null
          if (lastProductCopy.current) {
            const targetField = field === 'price' ? priceSanityField : field
            const fieldValue = lastProductCopy.current[targetField]
            lastFieldValue = fieldValue !== undefined ? fieldValue : null
          }

          const lastId = lastProductCopy.current?._id
          const lastInquiryOnly = lastProductCopy.current?.inquiryOnly

          const lastProduct = lastProductCopy.current
          const lastPlpRank =
            sortStrategy === PlpSortStrategy.Manual && lastProduct ? lastProduct.plpRank ?? 9999 : undefined

          return await getSanityProducts({
            capacity: capacity?.value,
            type: currentTypes,
            buildings: buildings,
            sortingParams: mapSortByType(sortBy.value),
            instantBook: instantBook,
            lastField: lastFieldValue,
            lastId: lastId !== undefined ? lastId : null,
            lastInquiryOnly: lastInquiryOnly !== undefined ? lastInquiryOnly : null,
            ...(lastPlpRank != null && { lastPlpRank }),
            paginated: true,
            sortStrategy,
          })
        },
        {
          staleTime: 5 * 60 * 1000,
        }
      )

      const lastProduct = data[data.length - 1]
      if (lastProduct) {
        lastProductCopy.current = lastProduct
      }

      if (isLoadingNewData) {
        productsData.current = data
      } else {
        // Deduplicate by _id to guard against overlapping cursor pages or
        // stale react-query cache returning products already in the list.
        const existingIds = new Set(productsData.current.map((p) => p._id))
        const newProducts = data.filter((p) => !existingIds.has(p._id))
        productsData.current = [...productsData.current, ...newProducts]
      }

      lastQueryKey.current = { location, building, typesKey, sortBy, capacity, instantBook, sortStrategy }
      await onProductsUpdate(productsData.current)
      setLoadingState(LoadingState.IDLE)
      // Set business hours if any selected type is meeting_room
      if (hasMeetingRoom) setBusinessHours(getBusinessHours(data))
    } catch (error) {
      setLoadingState(LoadingState.LOAD_ERROR)
    } finally {
      isFetching.current = false
    }
  }, [
    typesKey,
    location,
    building,
    sortBy,
    capacity,
    instantBook,
    queryClient,
    fetchProductsCount,
    buildingListCall,
    currentTypes,
    onProductsUpdate,
    hasMeetingRoom,
    resetPagination,
    sortStrategy,
  ])

  const fetchAllProducts = useCallback(async () => {
    try {
      setLoadingState(LoadingState.FETCHING_NEW_PRODUCTS)
      const data = await queryClient.fetchQuery(
        ['product', location, building, typesKey, sortBy, capacity, instantBook, sortStrategy],
        async () => {
          const b = buildingListCall()
          return await getAllSanityProducts({
            capacity: capacity?.value,
            type: currentTypes,
            buildings: b,
            sortingParams: mapSortByType(sortBy.value),
            instantBook: instantBook,
            sortStrategy,
          })
        },
        {
          staleTime: 5 * 60 * 1000,
        }
      )

      productsData.current = data
      await onProductsUpdate(data)
      setProductsSize(data.length)
      setLoadingState(LoadingState.IDLE)
      // Set business hours if any selected type is meeting_room
      if (hasMeetingRoom) setBusinessHours(getBusinessHours(data))
    } catch (error) {
      setLoadingState(LoadingState.LOAD_ERROR)
    }
  }, [
    queryClient,
    location,
    building,
    typesKey,
    sortBy,
    capacity,
    instantBook,
    buildingListCall,
    currentTypes,
    onProductsUpdate,
    hasMeetingRoom,
    sortStrategy,
  ])

  const buildingHolidays = useQuery({
    queryKey: ['buildingHolidays', building],
    queryFn: async () => getBuildingHolidays({ building: building as string }),
    enabled: !!building,
    staleTime: 5 * 60 * 1000,
  })

  return {
    loadingState,
    resetPagination,
    productsSize,
    fetchAllProducts,
    fetchProductsPaginated,
    buildingHolidays,
    businessHours,
  }
}
